<script setup>
import { storeToRefs } from 'pinia'
import { useAuthStore } from '~/stores/auth'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'

const props = defineProps({
    center: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                name: rawValue => rawValue?.name === undefined || typeof rawValue.name === 'string',
                uid: rawValue => rawValue?.uid === undefined || typeof rawValue.uid === 'string',
                logotype: rawValue => rawValue?.logotype === undefined || typeof rawValue.logotype === 'object',
                url: rawValue => rawValue?.logotype?.url === undefined || typeof rawValue.logotype.url === 'string',
                altText: rawValue => rawValue?.logotype?.altText === undefined || typeof rawValue.logotype.altText === 'string',
                selectCenter: rawValue => rawValue?.selectCenter === undefined || typeof rawValue.selectCenter === 'boolean',
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue.placeholder === 'boolean',
                contextConfig: rawValue => rawValue?.contextConfig === undefined || typeof rawValue.contextConfig === 'object'
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Define route
const localePath = useLocalePath()

// Fetch basic auth states
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Define favourite center cookie
const oneWeekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Current time + 1 week in milliseconds
const favouriteCenter = useCookie('favourite-center-cookie', getCookieOptions({ expires: oneWeekFromNow }))

// Load user data
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Get center list item config
const getCenterListItemConfig = computed(() => {
    return {
        selected: centerRoute.value === props?.center?.uid,
        placeholder: props.center.placeholder
    }
})

// Fetch profile mutation
const { mutate } = useUpdateProfileMutation({
    bearerToken,
    oid
})

/**
 * Selects a center by its short name and center name.
 *
 * @param {string} shortName - The short name of the center.
 * @param {string} centerName - The name of the center.
 * @return {Promise<void>} - A promise that resolves when the center is selected.
 */
const handleCenterSelection = async (shortName, centerName) => {
    // Check if click should fire
    if (!props.center?.selectCenter) return

    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.CENTER_SELECT,
        centerUid: shortName?.toUpperCase(),
        centerName: centerName,
        centerPreference: shortName?.toUpperCase()
    })

    // Save favourite center to cookie
    favouriteCenter.value = {
        centerRoute: props?.center?.uid,
    }

    // Check query params
    const defaultQueryParams = { 'center-details': null }
    const configQueryParams = props.center?.contextConfig?.queryParams

    const path = localePath({
        name: 'centerUid', // Correct route name as per your i18n configuration
        params: { centerUid: props?.center?.uid } // Pass dynamic centerUid
    })

    if (configQueryParams) {
        await navigateTo({
            path,
            query: configQueryParams // Add query parameters if available
        })
    } else {
        await navigateWithSidebarQuery({
            path,
            query: defaultQueryParams,
            route
        })
    }

    // Update user profile with new center selection
    if (isLoggedIn.value) {
        mutate({
            body: {
                centerCode: shortName
            }
        })
    }
}
</script>

<template>
    <ShadowContentBox
        class="center-list-item"
        :shadow-content-box-config="getCenterListItemConfig"
        @click="handleCenterSelection(center?.uid, center?.name)"
    >
        <div class="center-list-item-thumbnail-container">
            <component
                :is="SkeletonPlaceholder"
                v-if="center.placeholder"
                :skeleton-placeholder-config="{ type: 'image', width: '116px', height: '68px' }"
            />
            <img
                v-else
                :src="center?.logotype?.url"
                :alt="center?.logotype?.altText || center.name"
                width="116"
                height="68"
                class="center-list-item-thumbnail"
            >
        </div>
        <div class="center-list-item-content">
            <component
                :is="SkeletonPlaceholder"
                v-if="center.placeholder"
                :skeleton-placeholder-config="{ type: 'text' }"
            />
            <p v-else class="center-list-item-headline">
                {{ center.name }}
            </p>
        </div>
    </ShadowContentBox>
</template>

<style lang="scss">
.center-list-item {
    position: relative;
    display: grid;
    grid-template-columns: 145px 1fr;
    grid-template-areas: "thumbnail content";
    align-items: center;
    height: px2rem(100);
    margin-block-end: 15px;
}

.center-list-item-thumbnail-container {
    grid-area: thumbnail;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    isolation: isolate;
    padding: 15px;
}

.center-list-item-thumbnail {
    height: 68px;
    width: 116px;
    max-height: 80%;
    max-width: 80%;
    object-fit: contain;
}

.center-list-item-content {
    grid-area: content;
    overflow: hidden;
    padding: 0 15px 0 0;
}

.center-list-item-headline {
    margin: 0;
    padding-inline-end: 15px;

    @include body2-extrabold;
}

.center-list-item-subheadline {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-style: normal;
    margin: 0;

    @include body2-medium;
}
</style>
